//////////////////////////////////////////////////////////////////////////////
//
//  Response Interceptor
//
//  작성자: MyungHwan Kim
//  생성일: 2025-10-24
//
//  설명: Axios Response Interceptor
//       - 성공 응답 로깅
//       - 401 에러 시 Token Refresh
//       - 에러 처리
//
//////////////////////////////////////////////////////////////////////////////

import { AxiosInstance } from 'axios';
import { handleTokenRefresh, isTokenExpired } from './tokenRefreshHandler';
import logger from '../utils/logger';

/**
 * Response Interceptor 설정
 * @param axiosInstance Axios 인스턴스
 */
export const setupResponseInterceptor = (axiosInstance: AxiosInstance) => {
  axiosInstance.interceptors.response.use(
    response => {
      // 성공 응답 로깅 (개발 환경에서만)
      const isDev = process.env.NODE_ENV === 'development';
      if (isDev) {
        logger.debug(`📥 Response: ${response.status} ${response.config.url}`);
      }
      return response;
    },
    async error => {
      // 401 에러 발생 시 refresh token 시도
      const status = error.response?.status;
      const requestUrl = error.config?.url || '';

      // Refresh Token API 자체는 제외 (무한 루프 방지)
      const isRefreshTokenRequest = requestUrl.includes('Authentication/RefreshToken');

      // ✅ 로그인 API 호출 시 401은 정상적인 인증 실패이므로 Token Refresh 시도하지 않음
      const isLoginRequest = requestUrl.includes('Authentication/Login') || requestUrl.includes('Authentication/login');

      if (status === 401 && !isRefreshTokenRequest && !isLoginRequest) {
        // 무한 루프 방지: 이미 재시도된 요청이면 에러 반환
        if (error.config && error.config._retry) {
          logger.error('❌ Token refresh failed (Retry loop detected) - propagating error');
          return Promise.reject(error);
        }

        // 재시도 플래그 설정
        if (error.config) {
          error.config._retry = true;
        }

        logger.info('401 Unauthorized - attempting token refresh...');
        return await handleTokenRefresh(error, axiosInstance);
      }

      // 그 외 에러는 그대로 reject
      return Promise.reject(error);
    },
  );
};

export default setupResponseInterceptor;

